import { useState } from 'owa-react';
import { upNextRoot } from './getExtraTopRibbonControlsAfterTabs.scss';
import type { IButtonStyles } from '@fluentui/react';
import { UpNextV2 } from 'owa-upnext-v2';
import React from 'react';
import { MonarchWin32Toggle, lazyIsToggleEnabled } from 'native-newoutlook-toggle';
import { observer } from 'owa-mobx-react';
import { owaComputedFn } from 'owa-computed-fn';
import { isSafeModeSession } from 'owa-config/lib/isSafeModeSession';
import { isRecoveryModeSession } from 'owa-config/lib/isRecoveryModeSession';
import { SafeMode, RecoveryMode, ConfigJsonActive } from './getModeContainerComponents';
import { AutoToggleTeachingCallout } from 'native-newoutlook-teaching-callout';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import {
    canCallGetRuntimeModeStatus,
    getRuntimeModeStatus,
    RUNTIME_MODE_SAFE_MODE,
    RUNTIME_MODE_RECOVERY_MODE,
    RUNTIME_MODE_CONFIG_JSON_ACTIVE,
} from 'owa-client-native-interop/lib/diagnosticsInterop';

const upNextStyles: IButtonStyles = {
    root: upNextRoot,
};

const ExtraTopRibbonControlsAfterTabs = observer(function ExtraTopRibbonControlsAfterTabs() {
    const [toggleEnabled, setToggleEnabled] = useState(false);
    // Native-reported runtime modes. The native RuntimeModeStatus contract is the
    // single source of truth; these enhance (never replace) the synchronous
    // bO-query baseline below, so badges still render on older hosts that don't
    // implement the interop and never flicker off once the legacy signal is set.
    const [interopSafeMode, setInteropSafeMode] = useState(false);
    const [interopRecoveryMode, setInteropRecoveryMode] = useState(false);
    const [hasConfigJsonContent, setHasConfigJsonContent] = useState(false);
    const shouldShowConfigJsonIndicator =
        isGlobalFeatureEnabled('ring-dogfood') || isGlobalFeatureEnabled('ring-microsoft');

    React.useEffect(() => {
        const loadToggleEnabled = async () => {
            setToggleEnabled(await lazyIsToggleEnabled.importAndExecute());
        };
        loadToggleEnabled();
    }, []);
    React.useEffect(() => {
        let isDisposed = false;
        const loadRuntimeModeStatus = async () => {
            try {
                if (!(await canCallGetRuntimeModeStatus())) {
                    return;
                }

                const status = await getRuntimeModeStatus();
                if (isDisposed) {
                    return;
                }

                setInteropSafeMode(status.activeModes.includes(RUNTIME_MODE_SAFE_MODE));
                setInteropRecoveryMode(status.activeModes.includes(RUNTIME_MODE_RECOVERY_MODE));
                // ConfigJsonActive is only surfaced for internal rings, so only observe it there.
                if (shouldShowConfigJsonIndicator) {
                    setHasConfigJsonContent(
                        status.activeModes.includes(RUNTIME_MODE_CONFIG_JSON_ACTIVE)
                    );
                }
            } catch {
                // Best effort only; interop failures should not affect ribbon rendering.
            }
        };

        void loadRuntimeModeStatus();
        return () => {
            isDisposed = true;
        };
    }, [shouldShowConfigJsonIndicator]);

    // OR-merge the synchronous bO baseline with the native interop signal so the
    // interop can only add a badge, never remove one the legacy signal would show.
    const safeMode = isSafeModeSession() || interopSafeMode;
    const recoveryMode = isRecoveryModeSession() || interopRecoveryMode;
    const toggleRef = React.createRef<HTMLDivElement>();
    const autoToggleLightningId = 'AutoToggleTeachingCallout';

    return (
        <>
            {/* Each active mode renders its own badge; concurrent badges are
            intentional (e.g. a recovery session shows both Safe and Recovery). */}
            {safeMode && <SafeMode />}
            {recoveryMode && <RecoveryMode />}
            {shouldShowConfigJsonIndicator && hasConfigJsonContent && <ConfigJsonActive />}

            <UpNextV2
                containerStyle={upNextStyles}
                key="UpNextV2_ExtraTopRibbonControlsAfterTabs"
            />

            {toggleEnabled && (
                <div ref={toggleRef}>
                    <MonarchWin32Toggle />
                    {isGlobalFeatureEnabled('mail-auto-toggle-teaching-callout') && (
                        <AutoToggleTeachingCallout target={toggleRef} id={autoToggleLightningId} />
                    )}
                </div>
            )}
        </>
    );
}, 'ExtraTopRibbonControlsAfterTabs');

export const getExtraTopRibbonControlsAfterTabs: () => React.JSX.Element = owaComputedFn(
    function getExtraTopRibbonControlsAfterTabs(): React.JSX.Element {
        return <ExtraTopRibbonControlsAfterTabs />;
    }
);
